import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { defineMessages } from "react-intl";
import { animateScroll as scroll } from "react-scroll";
import { uniqueId } from "lodash";

import { useAlert } from "../Alerts/withAlert";
import { ALERT_TYPE_ERROR } from "../Alerts/Alert";
import { closest } from "../Utils/DomUtils";
import FormValidator from "../../helpers/FormValidator";
import { getDisplayName } from "../Utils/getDisplayName";
import { useFormatMessage } from "../Intl/Context";
import { useComponentIsUnmounted } from "../Utils/useComponentIsUnmounted";
import { useGetStickyElementsHeight } from "../Utils/StickyElementsHeight";

import {
  initializeForm as initializeFormAction,
  clearForm as clearFormAction
} from "../../reducers/form/initialize";
import { updateFieldSchema } from "../../reducers/form/schema";
import { updateFieldValue as updateFieldValueAction } from "../../reducers/form/fields";
import { updateFormErrors, updateFieldErrors } from "../../reducers/form/errors";

const FormStoreContext = createContext({});

const FormStoreConsumer = FormStoreContext.Consumer;
export { FormStoreConsumer };

export const useForm = () => {
  return useContext(FormStoreContext);
};

export const useFormField = fieldName => {
  const { formState } = useForm();

  return formState?.fields[fieldName] || {};
};

// Cache keys include a version prefix to allow migration when cached data
// becomes stale.
//
const FORM_KEY_PREFIX = "form:v1";

// When scrolling to an error field, give a 30px offset so that we fully see the target
// field.
const ERROR_FIELD_SCROLL_Y_OFFSET = 20;

export default function withFormStore(WrappedComponent, options = {}) {
  // Default to clearing form the form when the component is unmounted, but allow
  // customizing it when calling the `withFormStore` HOC.
  if (typeof options.clearFormOnUnmount === "undefined") {
    options.clearFormOnUnmount = true;
  }

  const WithFormStore = props => {
    const randomFormKeyRef = useRef();
    // If a formKey option was given to the withFormStore HOC, run the
    // provided function to create the form key.
    //
    // This allows to use server-side generated ids to restore previously
    // filled form inputs for the same form.
    //
    // If no formKey option was passed, then build a random formKey which will
    // only be used for the lifetime of the component
    //
    const formKey = useMemo(() => {
      if (options.formKey) {
        return `${FORM_KEY_PREFIX}:${options.formKey(props)}`;
      }

      // We avoid regenerating the `formKey` if it doesn't depend on the props, as if the
      // passed props change, but the `formKey` generator function prop isn't present, we
      // never want to update the `formKey`.
      if (!randomFormKeyRef.current) {
        const time = new Date().getTime();
        const random = Math.random(100000000);

        randomFormKeyRef.current = `${FORM_KEY_PREFIX}:${time}:${random}`;
      }

      return randomFormKeyRef.current;
    }, [props]);

    const dispatch = useDispatch();
    const isUnmounted = useComponentIsUnmounted();
    const { showAlert } = useAlert();

    const t = useFormatMessage();
    const formErrorMessage = t(intlMessages.formError);

    const formState = useSelector(({ forms }) => forms[formKey]);
    const { schema, fields } = formState || {};

    const validator = useMemo(() => {
      return new FormValidator(schema, fields);
    }, [fields, schema]);

    const getStickyElementsHeight = useGetStickyElementsHeight();

    const handleError = useCallback(() => {
      showAlert({
        message: formErrorMessage,
        type: ALERT_TYPE_ERROR
      });

      const firstError = document.querySelector(".form-group-invalid");
      if (!firstError) return;

      const parent =
        closest(
          firstError,
          el => el.className && el.className.match(/\bmodal-wrapper\b/)
        ) || window;

      if (parent !== window && !parent.id) {
        parent.id = uniqueId("modal");
      }

      const ignoredStickyElements =
        parent === window ? {} : { ignoreTopNavBar: true, ignoreStickyTitleBar: true };
      const topOffset = getStickyElementsHeight(ignoredStickyElements);

      const firstErrorY = firstError?.getBoundingClientRect()?.top || 0;
      const currentScrollTop =
        typeof parent.scrollTop === "undefined" ? parent.scrollY : parent.scrollTop;
      const offsetTop =
        firstErrorY + currentScrollTop - topOffset - ERROR_FIELD_SCROLL_Y_OFFSET;

      scroll.scrollTo(offsetTop, {
        containerId: parent === window ? void 0 : parent.id,
        duration: 300,
        smooth: true
      });
    }, [formErrorMessage, getStickyElementsHeight, showAlert]);

    const updateValidationErrors = useCallback(
      isValid => {
        dispatch(updateFormErrors(formKey, validator.errors));

        if (!isValid) {
          setTimeout(() => {
            if (isUnmounted()) return;

            handleError();
          }, 0);
        }
      },
      [dispatch, formKey, handleError, isUnmounted, validator.errors]
    );

    const validateForm = useCallback(() => {
      const isValid = validator.validate();

      updateValidationErrors(isValid);

      return isValid;
    }, [updateValidationErrors, validator]);

    const validateField = useCallback(
      fieldName => {
        const isValid = validator.validateField(fieldName);

        dispatch(updateFieldErrors(formKey, fieldName, validator.errors[fieldName]));

        return isValid;
      },
      [dispatch, formKey, validator]
    );

    const initializeForm = useCallback(() => {
      dispatch(initializeFormAction(formKey));
    }, [dispatch, formKey]);

    const clearForm = useCallback(() => {
      dispatch(clearFormAction(formKey));
    }, [dispatch, formKey]);

    const resetForm = useCallback(() => {
      clearForm();
      initializeForm();
    }, [clearForm, initializeForm]);

    const updateFieldValue = useCallback(
      (fieldName, value) => {
        dispatch(updateFieldValueAction(formKey, fieldName, value));
      },
      [dispatch, formKey]
    );

    const createField = useCallback(
      (fieldName, schema, value) => {
        dispatch(updateFieldSchema(formKey, fieldName, schema));
        updateFieldValue(fieldName, value);
      },
      [dispatch, formKey, updateFieldValue]
    );

    // This allows pushing errors from the server to the form store.
    const pushErrors = useCallback(
      errors => {
        const isValid = errors.length === 0;
        validator.pushErrors(errors);

        updateValidationErrors(isValid);
      },
      [validator, updateValidationErrors]
    );

    useEffect(
      () => () => {
        if (options.clearFormOnUnmount) clearForm();
      },
      [clearForm]
    );

    useEffect(() => {
      initializeForm();
    }, [initializeForm]);

    if (!formState) return null;

    return (
      <FormStoreContext.Provider
        value={{
          formKey,
          createField,
          updateFieldValue,
          formState,
          validateForm,
          validateField
        }}
      >
        <WrappedComponent
          {...{
            ...props,
            clearForm,
            resetForm,
            validateForm,
            validateField,
            pushErrors,
            formState
          }}
        />
      </FormStoreContext.Provider>
    );
  };

  WithFormStore.displayName = `WithFormStore(${getDisplayName(WrappedComponent)})`;

  return WithFormStore;
}

const intlMessages = defineMessages({
  formError: {
    id: "withFormStore.formError",
    defaultMessage: `
      Le formulaire n'a pas pu être soumis car il contient des erreurs. Merci
      de vérifier les champs du formulaire et d'essayer de le soumettre à
      nouveau.
    `
  }
});
